import FPVue from "../FP.vue";
import serviceErrorHandling from "../mixins/serviceErrorHandling";
import { hubService, HubWithDetails, propertyService, PropertyWithDetails } from "../services";
import ApiKeyDialog from "./dialogs/AL.ApiKeyDialog.vue";

type PropertyWithOwnerName = PropertyWithDetails & {
  summaryString: string;
  ownerName: string;
};
type ShellCommandGroup = {
  name: string;
  commands: Array<ShellCommandOption>;
};
type ShellCommandOption = {
  name: string;
  command: string;
  requiresValue?: boolean;
};
export default FPVue.extend({
  name: "al-mqtt-shell-commands",

  mixins: [serviceErrorHandling],
  components: { "al-api-key-dialog": ApiKeyDialog },

  data: function() {
    return {
      rules: {
        required: (value: any) => !!value || "Required."
      },
      showApiKeyDialog: false,
      apiKey: "" as string,
      dataLoaded: false,

      sendingCommand: false,
      properties: [] as PropertyWithOwnerName[],
      selectedPropertyID: null as string | null | undefined,
      hubs: [] as HubWithDetails[],
      selectedHubID: null as string | null,

      selectedCommand: null as ShellCommandOption | null,
      commandValue: "",
      commandResult: null as string | null,

      versionCommand: { name: "Version", command: "ver" } as ShellCommandOption,
      checkForFirmwareCommand: {
        name: "Trigger Firmware Check",
        command: "checkfw"
      } as ShellCommandOption,
      updateHubFwCommand: {
        name: "Trigger Hub FW Update",
        command: "dfu start"
        // requiresValue: true
      } as ShellCommandOption,
      updateControllerFwCommand: {
        name: "Trigger Controller FW Update",
        command: "cdfu start"
      } as ShellCommandOption,
      factoryRestoreCommand: {
        name: "Factory Restore",
        command: "factory_restore"
      } as ShellCommandOption,
      rebootCommand: {
        name: "Reboot",
        command: "kernel reboot cold"
      } as ShellCommandOption,
      cdfuCommand: {
        name: "Controller DFU file version",
        command: "cdfu ver"
      } as ShellCommandOption,
      appSettingsOptionsGroup: {
        name: "App Settings",
        commands: [
          {
            name: "Timezone",
            command: "appsettings get timezone"
          } as ShellCommandOption,
          {
            name: "Get Hub Firmware Url",
            command: "appsettings get hubCheckUrl"
          } as ShellCommandOption,
          {
            name: "Set New Hub Firmware Url",
            command: "appsettings set hubCheckUrl",
            requiresValue: true
          } as ShellCommandOption,
          {
            name: "Get controller Firmware Url",
            command: "appsettings get controllerCheckUrl"
          } as ShellCommandOption,
          {
            name: "Set New controller Firmware Url",
            command: "appsettings set controllerCheckUrl",
            requiresValue: true
          } as ShellCommandOption
        ]
      } as ShellCommandGroup
    };
  },
  watch: {
    apiKey(newValue, oldValue) {
      if (newValue == oldValue) return;

      // If the API key changes, clear the data
      this.clearData();
    },
    selectedCommand: function() {
      this.commandResult = null;
      this.commandValue = "";
    },
    selectedPropertyID: async function(newValue, oldValue) {
      if (oldValue == newValue) return;
      this.hubs = [];
      this.selectedHubID = null;

      this.inlineMessage.message = null;
      if (!this.selectedPropertyID?.length) return;

      this.processing = true;
      try {
        await this.loadHubs();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.processing = false;
      }
      if (!!this.hubs?.length) this.selectedHubID = this.hubs[0].id!;
    }
  },

  computed: {
    isSelectedCommandValid(): boolean {
      if (!this.selectedCommand) return false;
      if (!!this.selectedCommand.requiresValue && !this.commandValue?.length) return false;
      return true;
    },
    commandText(): string {
      if (!this.isSelectedCommandValid) return "";
      return `${this.selectedCommand!.command} ${this.commandValue}`.trim();
    },
    commandOptions(): (
      | ShellCommandOption
      | {
          divider?: boolean;
          header?: string;
        }
    )[] {
      var commands: (
        | ShellCommandOption
        | {
            divider?: boolean;
            header?: string;
          }
      )[] = [];

      commands.push({
        header: this.appSettingsOptionsGroup.name
      });
      commands.push({
        divider: true
      });
      commands = commands.concat(this.appSettingsOptionsGroup.commands);

      commands.push({
        header: "General"
      });
      commands.push({
        divider: true
      });
      commands = commands.concat([
        this.versionCommand,
        this.factoryRestoreCommand,
        this.rebootCommand,
        this.cdfuCommand,
        this.checkForFirmwareCommand,
        this.updateHubFwCommand,
        this.updateControllerFwCommand
      ]);
      return commands;
    }
  },

  methods: {
    async clearData() {
      this.properties = [];
      this.selectedCommand = null;
      // Clearing the selectedPropertyID automatically clears the hubs and selectedHubID values
      this.selectedPropertyID = null;

      this.dataLoaded = false;
    },
    async loadData() {
      if (!this.apiKey) return;

      this.processing = true;
      try {
        const properties = await propertyService.getAllForShellCommandsUsingApiToken(this.apiKey);
        this.properties = properties
          .sort(
            (a: { name: string | null | undefined }, b: { name: string | null | undefined }) => {
              let aName = a.name!.toLocaleLowerCase();
              let bName = b.name!.toLocaleLowerCase();
              if (aName < bName) return -1;
              else if (aName > bName) return 1;
              else return 0;
            }
          )
          .map(x => ({
            ...x,
            ownerName: `${x.owner.firstName} ${x.owner.lastName}`,
            summaryString: `${x.name} (${x.owner.firstName} ${x.owner.lastName})`
          }));
        if (!!this.properties?.length) this.selectedPropertyID = this.properties[0].id!;

        this.dataLoaded = true;
      } catch (error) {
        this.handleError(error as Error);
      } finally {
        this.processing = false;
      }
    },
    async loadHubs() {
      if (!this.apiKey) return;

      if (!this.selectedPropertyID?.length) return;
      this.hubs = await hubService.getPropertyRegisteredHubsForShellCommandsUsingApiToken(
        this.selectedPropertyID,
        this.apiKey
      );
      this.selectedHubID = null;
    },
    async sendCommand() {
      if (!this.apiKey) return;

      this.inlineMessage.message = null;
      if (!this.commandText?.length || !this.selectedHubID) {
        this.inlineMessage.message = "Unable to send command - no command text or no hub.";
        return;
      }
      this.processing = true;
      this.sendingCommand = true;
      try {
        this.commandResult = await hubService.sendShellCommandToHubUsingApiToken(
          this.selectedHubID,
          this.commandText,
          this.apiKey
        );
      } catch (error) {
        this.handleError(error);
      } finally {
        this.processing = false;
        this.sendingCommand = false;
      }
    }
  },
  created: async function() {}
});

